/**
 * Utilitários para gerenciamento de imagens em diferentes ambientes
 * Suporta duas estruturas de pastas de imagens:
 * 1. Em desenvolvimento: /imagens/ (dentro da pasta public)
 * 2. Em produção: /imagens/ (pasta raiz do projeto)
 */

// Detectar ambiente automaticamente
const isProduction = window.location.hostname !== 'localhost';

// Cache para URLs de imagens (limitado a 500 entradas)
const imageUrlCache = {};
let cacheEntries = 0;
const MAX_CACHE_SIZE = 500;

// Lista de imagens prioritárias
const priorityImages = new Set();

// Cache de imagens em memória
const imageCache = new Map();

/**
 * Marca uma imagem como prioritária para carregamento
 * @param {string} path - Caminho da imagem
 */
export const setPriorityImage = (path) => {
  if (!path) return;
  priorityImages.add(path);
};

/**
 * Limpa o cache de URLs de imagens
 */
export const clearImageCache = () => {
  Object.keys(imageUrlCache).forEach(key => {
    delete imageUrlCache[key];
  });
  cacheEntries = 0;
};

/**
 * Obtém a URL para uma imagem, com suporte a redimensionamento
 * @param {string} path - Caminho da imagem
 * @param {number} width - Largura desejada (opcional)
 * @returns {string} URL da imagem
 */
export const getImageUrl = (path, width) => {
  if (!path) {
    return '';
  }

  const cacheKey = `${path}_${width || 'original'}`;
  if (imageUrlCache[cacheKey]) {
    return imageUrlCache[cacheKey];
  }

  if (path.startsWith('http://') || path.startsWith('https://')) {
    imageUrlCache[cacheKey] = path;
    return path;
  }

  // Pega apenas o nome do arquivo, ignorando o resto do caminho.
  const filename = path.split('/').pop();

  // Constrói a URL a partir de uma base limpa.
  let finalUrl = `/imagens/${filename}`;

  // Adiciona o prefixo do servidor de desenvolvimento, se aplicável.
  if (!isProduction) {
    finalUrl = `http://localhost:8080/catalogo${finalUrl}`;
  }

  if (width) {
    finalUrl += `?width=${width}`;
  }

  // Gerenciamento de cache
  if (cacheEntries >= MAX_CACHE_SIZE) {
    const keysToRemove = Object.keys(imageUrlCache).slice(0, Math.floor(MAX_CACHE_SIZE * 0.2));
    keysToRemove.forEach(key => delete imageUrlCache[key]);
    cacheEntries -= keysToRemove.length;
  }

  imageUrlCache[cacheKey] = finalUrl;
  cacheEntries++;

  return finalUrl;
};

/**
 * Função para codificar corretamente strings Unicode para base64
 * @param {string} str - String para codificar
 * @returns {string} String codificada em base64
 */
const utf8ToBase64 = (str) => {
  return window.btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => {
    return String.fromCharCode('0x' + p1);
  }));
};



/**
 * Cria uma imagem SVG de fallback com o nome do produto
 * @param {string} name - Nome do produto para exibir no SVG
 * @param {number} width - Largura do SVG
 * @param {number} height - Altura do SVG
 * @returns {string} URL da imagem SVG em formato data:image/svg+xml
 */
export const createFallbackImage = (name, width = 300, height = 300) => {
  if (!name) name = 'Produto';
  
  // Usar apenas as primeiras palavras do nome para evitar textos muito longos
  const shortName = name.split(' ').slice(0, 2).join(' ');
  
  // Gerar uma cor de fundo baseada no nome do produto para ter variação visual
  const getColorFromString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Gerar cores em tons de rosa/roxo para combinar com a identidade visual da Floratta
    const h = Math.abs(hash) % 60 + 300; // Tons entre roxo e rosa (300-360)
    const s = 70 + (Math.abs(hash) % 20); // Saturação entre 70-90%
    const l = 75 + (Math.abs(hash) % 10); // Luminosidade entre 75-85%
    
    return `hsl(${h}, ${s}%, ${l}%)`;
  };
  
  // Obter uma cor baseada no nome do produto
  const bgColor = getColorFromString(name);
  const textColor = '#fff'; // Texto branco para contrastar
  
  // Criar um SVG mais atraente com ícone e gradiente
  const svgContent = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:${bgColor};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${bgColor};stop-opacity:0.8" />
        </linearGradient>
      </defs>
      <rect width="${width}" height="${height}" fill="url(#grad)" rx="10" ry="10"/>
      <circle cx="${width/2}" cy="${height/3}" r="${width/8}" fill="rgba(255,255,255,0.2)"/>
      <text x="50%" y="${height/3}" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="${Math.floor(width/8)}" fill="${textColor}" font-weight="bold">🛍️</text>
      <text x="50%" y="65%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="${Math.floor(width/16)}" fill="${textColor}" font-weight="bold">${shortName}</text>
      <text x="50%" y="80%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="${Math.floor(width/20)}" fill="${textColor}">Floratta</text>
    </svg>
  `;
  
  return `data:image/svg+xml;base64,${utf8ToBase64(svgContent)}`;
};
